import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { Modal } from 'react-bootstrap';
import Flexbox from 'flexbox-react';
import MainMenuItem from 'components/core/header/MainMenuItem';
import ProcessValidator from 'utils/ProcessValidator';
import ContactService from 'services/ContactService';
import SecurityService from 'services/SecurityService';

/**
 * Renders the Contact Us component
 */
export default class ContactUs extends MainMenuItem {
    static propTypes = {
        username: PropTypes.string,
        users: PropTypes.object,
        icon: PropTypes.bool
    };

    static defaultProps = {
        icon: true
    };

    constructor(props) {
        super(props);

        this.state = {
            formOpen: false
        };

        this.formComponent = null;
    }

    componentWillMount() {
        SecurityService.fetchUsersIfNeeded();
    }

    onContactUs = () => {
        this.setState({ formOpen: true });
    };

    onSend = () => {
        const invalid = this.formComponent.validateFields();

        if (invalid) {
            this.formComponent.setInvalid();
            return false;
        } else {
            const values = this.formComponent.getValues();
            ContactService.contact(values.name, values.email, values.message);
            this.setState({ formOpen: false });
            return true;
        }
    };

    onCancel = () => {
        this.setState({ formOpen: false });
    };

    renderContactForm = () => {
        const formRefFunction = (component) => { this.formComponent = component; };

        let displayName = '';
        let userEmail = '';
        if (this.props.users) {
            if (this.props.users[this.props.username]){
                const userObj = this.props.users[this.props.username];
                if (userObj.firstName && userObj.lastName) {
                    displayName = `${userObj.firstName} ${userObj.lastName}`;
                }

                if (userObj.email) {
                    userEmail = userObj.email;
                }
            }
        }

        return (
            <div className="static-modal contact-us">
                <Modal.Dialog ref="dialog">
                    <Modal.Header>
                        <Modal.Title>Contact Us</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <Flexbox flexDirection="column" className="scrollable">
                            <ContactUsForm
                                displayName={displayName}
                                userEmail={userEmail}
                                ref={formRefFunction}
                            />
                        </Flexbox>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button onClick={this.onSend} type="primary">Send</Button>
                        <Button onClick={this.onCancel}>Cancel</Button>
                    </Modal.Footer>

                </Modal.Dialog>
            </div>
        );
    };

    renderMenuComponent() {
        const icon = this.props.icon ?  <i className="fa fa-envelope fa-lg" /> : '';
        return (
            <span>
                <a onClick={this.onContactUs} title="Contact us with a question or problem">
                    <span>
                        {icon} Contact Us
                    </span>
                </a>
                { this.state.formOpen ? this.renderContactForm() : '' }
            </span>
        );
    }
}

class ContactUsForm extends React.Component {
    static propTypes = {
        displayName: PropTypes.string,
        userEmail: PropTypes.string
    };

    constructor(props) {
        super(props);

        this.state = {
            content: 'form', //form, sending, or message
            invalid: false,
            name: props.displayName,
            email: props.userEmail,
            message: '',
            validation: {
                name: { valid: true, message: '' },
                email: { valid: true, message: '' },
                message: { valid: true, message: '' }
            }
        };

        this.autoFocus = true;
        this.validator = new ProcessValidator(null);
    }

    componentDidMount() {
        this.autoFocus = false;
    }

    onInputChange = (e) => {
        const target = e.target;
        const value = target.value;
        const key = target.name;

        this.validateField(key, value);

        this.setState({
            [key]: value
        });
    };

    getValues = () => {
        return {
            name: this.state.name,
            email: this.state.email,
            message: this.state.message
        };
    };

    setInvalid = () => {
        this.setState({ invalid: true });
    };

    setContent = (content) => {
        this.setState({ content });
    };

    validateField = (key, value) => {
        const validation = this.state.validation;

        if (value === '') {
            validation[key] = { valid: false, message: `${key.charAt(0).toUpperCase() + key.slice(1)} field cannot be blank` };
        } else if (key === 'email') {
            const results = this.validator.validateEmail(value);
            if (results !== null) {
                validation[key] = { valid: false, message: results.message };
            } else {
                validation[key] = { valid: true, message: '' };
            }
        } else {
            validation[key] = { valid: true, message: '' };
        }
    };

    validateFields = () => {
        const values = this.getValues();
        let invalid = false;

        Object.keys(values).forEach(key => {
            this.validateField(key, values[key]);
        });

        Object.keys(this.state.validation).forEach(key => {
            if (!this.state.validation[key].valid) {
                invalid = true;
            }
        });

        return invalid;
    };

    render = () => {
        const errorMessageRefFunction = (component) => {this.errorMessageComponent = component;};
        const errorMessage = [];
        let errorMessageClass = '';
        Object.keys(this.state.validation).forEach(key => {
            if (!this.state.validation[key].valid) {
                errorMessageClass = 'invalid';
                errorMessage.push(<p key={key}>{this.state.validation[key].message}</p>);
            }
        });

        return (
            <Flexbox flexDirection="column" className="contact-us-form">
                <Flexbox alignItems="baseline" flexDirection="row" style={{ marginBottom: '10px' }}>
                    <input
                        name="name"
                        placeholder="Enter your name..."
                        className={ `form-control ${this.state.validation.name.valid ? '' : 'invalid'}` }
                        style={{ flexGrow: 1 }}
                        onChange={this.onInputChange}
                        value={this.state.name}
                        autoFocus={this.autoFocus}
                    />
                </Flexbox>
                <Flexbox alignItems="baseline" flexDirection="row" style={{ marginBottom: '20px' }}>
                    <input
                        name="email"
                        placeholder="Enter your email address..."
                        className={ `form-control ${this.state.validation.email.valid ? '' : 'invalid'}` }
                        style={{ flexGrow: 1 }}
                        onChange={this.onInputChange}
                        value={this.state.email}
                    />
                </Flexbox>
                <Flexbox alignItems="baseline" flexDirection="row">
                    <textarea
                        name="message"
                        rows={6}
                        style={{ flexGrow: 1 }}
                        className={ `form-control ${this.state.validation.message.valid ? '' : 'is-invalid'}` }
                        placeholder="Please describe in detail your problem or question..."
                        value={this.state.message}
                        onInput={this.onInputChange}
                    />
                </Flexbox>
                <div className={errorMessageClass} style={ { marginTop: '15px' } } ref={errorMessageRefFunction}>
                    {errorMessage}
                </div>
            </Flexbox>
        );
    };
}



// WEBPACK FOOTER //
// ./src/components/core/header/ContactUs.js